import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

const defaultPortal = (u) => {
  if (!u) return "company";
  const saved = localStorage.getItem("ct_portal");
  if (u.can_switch_portal && (saved === "company" || saved === "customer")) return saved;
  return u.roles?.includes("customer_admin") ? "customer" : "company";
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portal, setPortalState] = useState("company");

  const setPortal = useCallback((next) => {
    localStorage.setItem("ct_portal", next);
    setPortalState(next);
  }, []);

  const apply = useCallback((u) => {
    setUser(u);
    setPortalState(defaultPortal(u));
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => apply(data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [apply]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.removeItem("ct_portal");
      apply(data.user);
      return data.user;
    },
    [apply]
  );

  // Used after flows where the backend already signed the user in (setup link, signup).
  const adopt = useCallback(
    (u) => {
      localStorage.removeItem("ct_portal");
      apply(u);
    },
    [apply]
  );

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem("ct_portal");
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, portal, setPortal, refreshUser, adopt }),
    [user, loading, login, logout, portal, setPortal, refreshUser, adopt]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
